import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileText,
  ListChecks,
  Mic,
  MicOff,
  RotateCcw,
  Save,
  Volume2,
  WandSparkles,
  X
} from "lucide-react";

const guidedTurns = [
  {
    id: "topic",
    label: "Konu",
    prompt: "Önce ele alacağımız konunun adını ve müşterinin temel sorusunu anlatın.",
    placeholder: "Örn. Yüzde üç fon yükümlülüğü nedir?"
  },
  {
    id: "applicability",
    label: "Kapsam",
    prompt: "Bu kural veya süreç kimleri, hangi firmaları ve hangi koşullarda ilgilendiriyor?",
    placeholder: "Kimler için geçerli, hangi eşik veya koşullar var?"
  },
  {
    id: "legalBasis",
    label: "Dayanak",
    prompt: "Mevzuat dayanağını ve doğrulanmış açıklamayı kendi ifadelerinizle anlatın.",
    placeholder: "Kanun, yönetmelik veya uygulama açıklaması..."
  },
  {
    id: "steps",
    label: "Adımlar",
    prompt: "Süreci baştan sona, mümkünse birinci adım, ikinci adım şeklinde anlatın.",
    placeholder: "1. Beyan kontrol edilir\n2. Tutar hesaplanır\n3. Belge yüklenir"
  },
  {
    id: "deadline",
    label: "Süreler",
    prompt: "Son tarihleri, bildirim zamanlarını ve varsa tekrar sıklığını anlatın.",
    placeholder: "Son tarih, hatırlatma başlangıcı ve tekrar bilgisi..."
  },
  {
    id: "exceptions",
    label: "İstisnalar",
    prompt: "İstisnaları, sık yapılan hataları ve dikkat edilmesi gereken noktaları anlatın.",
    placeholder: "İstisnalar, riskler ve sık yapılan hatalar..."
  }
];

const initialResponses = Object.fromEntries(guidedTurns.map((turn) => [turn.id, ""]));

const cleanInline = (value) => String(value || "")
  .replace(/\s+([,.!?;:])/g, "$1")
  .replace(/[ \t]+/g, " ")
  .trim();

const cleanMultiline = (value) => String(value || "")
  .replace(/\r\n?/g, "\n")
  .split("\n")
  .map((line) => cleanInline(line))
  .join("\n")
  .replace(/\n{3,}/g, "\n\n")
  .trim();

const asQuestion = (value) => {
  const clean = cleanInline(value).replace(/[.!]+$/, "");
  if (!clean) return "Bu süreç nasıl uygulanır?";
  return `${clean}${clean.endsWith("?") ? "" : "?"}`;
};

const extractSteps = (value) => {
  const text = cleanMultiline(value);
  if (!text) return [];
  const lines = text.split("\n").map((line) =>
    line.replace(/^\s*(?:\d+[.)-]?|[-•*])\s*/, "").trim()
  ).filter(Boolean);
  if (lines.length > 1) return lines;
  const numbered = text.split(/(?:^|\s)(?:\d+)[.)-]\s+/).map(cleanInline).filter(Boolean);
  if (numbered.length > 1) return numbered;
  return text.split(/(?<=[.!?])\s+(?=[A-ZÇĞİÖŞÜ])/).map(cleanInline).filter(Boolean);
};

const buildDrafts = (responses, category, sourceTitle) => {
  const topic = cleanInline(responses.topic);
  const sections = [
    ["Kimleri ilgilendirir?", responses.applicability],
    ["Açıklama ve dayanak", responses.legalBasis],
    ["Uygulama adımları", responses.steps],
    ["Süre ve son tarihler", responses.deadline],
    ["Dikkat edilmesi gerekenler", responses.exceptions]
  ].filter(([, value]) => cleanMultiline(value));
  const fullAnswer = sections.map(([title, value]) => `${title}\n${cleanMultiline(value)}`).join("\n\n");
  const common = {
    category: cleanInline(category) || "Genel",
    sourceTitle: cleanInline(sourceTitle) || "4691 Teknopark Kanunu",
    validFrom: "2000-01-01",
    validUntil: "2028-12-31",
    status: "draft",
    tags: []
  };
  const drafts = [{
    ...common,
    question: asQuestion(topic),
    alternativeQuestions: topic ? [`${topic} nasıl uygulanır?`, `${topic} için nelere dikkat edilmelidir?`] : [],
    answer: fullAnswer || cleanMultiline(responses.legalBasis)
  }];
  [
    ["applicability", `${topic} kimleri ve hangi durumları kapsar?`],
    ["steps", `${topic} sürecinin adımları nelerdir?`],
    ["deadline", `${topic} için süreler ve son tarihler nelerdir?`],
    ["exceptions", `${topic} sürecinde dikkat edilmesi gerekenler nelerdir?`]
  ].forEach(([id, question]) => {
    if (cleanMultiline(responses[id])) {
      drafts.push({ ...common, question: asQuestion(question), alternativeQuestions: [], answer: cleanMultiline(responses[id]) });
    }
  });
  return drafts.filter((draft) => draft.question && draft.answer);
};

function VoiceKnowledgeBuilder({
  themeMode = "light",
  categoryOptions = [],
  onClose,
  onUseDraft,
  onSaveDrafts
}) {
  const [stage, setStage] = useState("session");
  const [activeTurn, setActiveTurn] = useState(0);
  const [responses, setResponses] = useState(initialResponses);
  const [category, setCategory] = useState("Genel");
  const [sourceTitle, setSourceTitle] = useState("4691 Teknopark Kanunu");
  const [qaDrafts, setQaDrafts] = useState([]);
  const [processDraft, setProcessDraft] = useState(null);
  const [recordingTarget, setRecordingTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const recognitionRef = useRef(null);
  const dark = themeMode === "dark";
  const colors = {
    text: dark ? "#e5edf6" : "#123f50",
    muted: dark ? "#9fb0c4" : "#64748b",
    surface: dark ? "#162133" : "#ffffff",
    subtle: dark ? "#203047" : "#f8fafc",
    border: dark ? "#334155" : "#dbe2ea",
    accent: "#0f766e"
  };
  const currentTurn = guidedTurns[activeTurn];

  const SpeechRecognition = useMemo(() => {
    if (typeof window === "undefined") return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
  }, []);

  useEffect(() => () => {
    recognitionRef.current?.abort?.();
    window.speechSynthesis?.cancel?.();
  }, []);

  const speak = (text, onEnd) => {
    if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) {
      onEnd?.();
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = "tr-TR";
    utterance.rate = 0.96;
    utterance.onend = () => onEnd?.();
    utterance.onerror = () => onEnd?.();
    window.speechSynthesis.speak(utterance);
  };

  const stopListening = () => {
    recognitionRef.current?.stop?.();
    setRecordingTarget("");
  };

  const startRecognition = (turnId, append = true) => {
    if (!SpeechRecognition) {
      setError("Bu tarayıcı ses tanımayı desteklemiyor. Güncel Google Chrome ile tekrar deneyebilirsiniz.");
      return;
    }
    recognitionRef.current?.abort?.();
    setError("");
    const recognition = new SpeechRecognition();
    recognition.lang = "tr-TR";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    let settled = append ? cleanMultiline(responses[turnId]) : "";

    recognition.onstart = () => setRecordingTarget(turnId);
    recognition.onresult = (event) => {
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index][0]?.transcript || "";
        if (event.results[index].isFinal) settled = cleanMultiline(`${settled}${settled ? "\n" : ""}${transcript}`);
        else interim += transcript;
      }
      setResponses((current) => ({
        ...current,
        [turnId]: cleanMultiline(`${settled}${interim ? `${settled ? "\n" : ""}${interim}` : ""}`)
      }));
    };
    recognition.onerror = (event) => {
      if (["not-allowed", "service-not-allowed"].includes(event.error)) {
        setError("Mikrofon izni verilmedi. Adres çubuğundaki site izinlerinden mikrofonu açın.");
      } else if (!["aborted", "no-speech"].includes(event.error)) {
        setError(`Ses tanıma tamamlanamadı: ${event.error}`);
      }
    };
    recognition.onend = () => {
      setRecordingTarget("");
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    try { recognition.start(); } catch (reason) { setError("Mikrofon başlatılamadı. Lütfen tekrar deneyin."); }
  };

  const askAndListen = () => speak(currentTurn.prompt, () => startRecognition(currentTurn.id));

  const goNext = () => {
    stopListening();
    if (activeTurn < guidedTurns.length - 1) {
      setActiveTurn((value) => value + 1);
      setTimeout(() => speak(guidedTurns[activeTurn + 1].prompt), 200);
      return;
    }
    const generated = buildDrafts(responses, category, sourceTitle);
    const steps = extractSteps(responses.steps);
    if (!cleanInline(responses.topic) || !cleanMultiline(responses.legalBasis)) {
      setError("Taslak oluşturmak için en az Konu ve Dayanak cevaplarını doldurun.");
      return;
    }
    setQaDrafts(generated);
    setProcessDraft({
      title: cleanInline(responses.topic).replace(/\?$/, ""),
      category: cleanInline(category) || "Genel",
      sourceTitle: cleanInline(sourceTitle) || "4691 Teknopark Kanunu",
      overview: cleanMultiline(responses.legalBasis),
      applicability: cleanMultiline(responses.applicability),
      legalBasis: cleanMultiline(responses.legalBasis),
      deadline: cleanMultiline(responses.deadline),
      exceptions: cleanMultiline(responses.exceptions),
      steps: steps.map((title) => ({ title, completed: false })),
      status: "draft"
    });
    setStage("review");
    setError("");
    speak("Oturumdan soru cevap ve süreç taslakları oluşturuldu. Kaydetmeden önce kontrol edebilirsiniz.");
  };

  const updateQADraft = (index, field, value) => setQaDrafts((current) =>
    current.map((draft, draftIndex) => draftIndex === index ? { ...draft, [field]: value } : draft)
  );

  const updateProcessStep = (index, value) => setProcessDraft((current) => ({
    ...current,
    steps: current.steps.map((step, stepIndex) => stepIndex === index ? { ...step, title: value } : step)
  }));

  const saveAll = async () => {
    if (!qaDrafts.length || !processDraft?.steps?.length) {
      setError("En az bir soru-cevap ve bir süreç adımı bulunmalıdır.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSaveDrafts?.(qaDrafts, processDraft);
    } catch (reason) {
      setError(reason?.message || "Taslaklar kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    stopListening();
    setResponses(initialResponses);
    setActiveTurn(0);
    setQaDrafts([]);
    setProcessDraft(null);
    setStage("session");
    setError("");
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Sesli bilgi oturumu" style={overlay}>
      <div style={{ width: "min(1100px, calc(100vw - 28px))", maxHeight: "calc(100vh - 30px)", overflowY: "auto", borderRadius: 24, background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`, boxShadow: "0 28px 80px rgba(15,23,42,.3)" }}>
        <header style={{ padding: "18px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <span style={{ width: 44, height: 44, borderRadius: 14, display: "grid", placeItems: "center", background: "linear-gradient(135deg,#0f766e,#22c1b7)", color: "#fff" }}><WandSparkles size={22}/></span>
            <div>
              <h3 style={{ margin: 0 }}>Teknopark AI Bilgi Oturumu</h3>
              <div style={{ color: colors.muted, fontSize: 12, marginTop: 4 }}>Konuşmadan çoklu soru-cevap ve süreç şablonu oluşturun</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Pencereyi kapat" style={iconButton(colors)}><X size={18}/></button>
        </header>

        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
            <div style={stagePill(stage === "session", colors)}><Mic size={16}/> 1. Bilgi görüşmesi</div>
            <div style={stagePill(stage === "review", colors)}><CheckCircle2 size={16}/> 2. Taslakları kontrol et</div>
          </div>
          {error && <div style={{ padding: 11, borderRadius: 11, background: "#fee2e2", color: "#991b1b", fontWeight: 700, marginBottom: 13 }}>{error}</div>}

          {stage === "session" ? (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(210px,260px) minmax(0,1fr)", gap: 16 }}>
              <aside style={{ border: `1px solid ${colors.border}`, borderRadius: 16, padding: 10, background: colors.subtle }}>
                {guidedTurns.map((turn, index) => (
                  <button key={turn.id} onClick={() => { stopListening(); setActiveTurn(index); }} style={turnButton(index === activeTurn, Boolean(responses[turn.id]), colors)}>
                    <span style={{ width: 25, height: 25, borderRadius: "50%", display: "grid", placeItems: "center", background: responses[turn.id] ? "#dcfce7" : colors.surface, color: responses[turn.id] ? "#166534" : colors.text }}>{responses[turn.id] ? <CheckCircle2 size={15}/> : index + 1}</span>
                    <span>{turn.label}</span>
                  </button>
                ))}
                <div style={{ margin: "12px 5px 2px", color: colors.muted, fontSize: 12, lineHeight: 1.5 }}>Ses kaydı saklanmaz. Yalnızca tarayıcının oluşturduğu metin taslağa alınır.</div>
              </aside>

              <main>
                <div style={{ borderRadius: 16, padding: 16, background: dark ? "#12323c" : "#ecfeff", border: `1px solid ${dark ? "#285467" : "#a5f3fc"}`, marginBottom: 14 }}>
                  <div style={{ display: "flex", gap: 9 }}><Volume2 size={19} color="#0f766e"/><div><strong>Argevio soruyor</strong><p style={{ margin: "5px 0 0", color: colors.muted, lineHeight: 1.55 }}>{currentTurn.prompt}</p></div></div>
                </div>
                <label style={{ fontWeight: 850 }}>{currentTurn.label}
                  <textarea
                    value={responses[currentTurn.id]}
                    onChange={(event) => setResponses((current) => ({ ...current, [currentTurn.id]: event.target.value }))}
                    placeholder={currentTurn.placeholder}
                    style={{ ...inputStyle(colors), minHeight: currentTurn.id === "steps" ? 210 : 165 }}
                  />
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 12 }}>
                  <label style={{ fontWeight: 800 }}>Kategori
                    <input list="voice-session-category-options" value={category} onChange={(event) => setCategory(event.target.value)} style={inputStyle(colors)}/>
                    <datalist id="voice-session-category-options">{categoryOptions.map((item) => <option key={item} value={item}/>)}</datalist>
                  </label>
                  <label style={{ fontWeight: 800 }}>Kaynak adı
                    <input value={sourceTitle} onChange={(event) => setSourceTitle(event.target.value)} style={inputStyle(colors)}/>
                  </label>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {recordingTarget ? <button onClick={stopListening} style={recordingButton}><MicOff size={18}/> Kaydı bitir</button> : <button onClick={askAndListen} style={primaryButton}><Mic size={18}/>{responses[currentTurn.id] ? "Konuşmaya devam et" : "Yanıtla"}</button>}
                    <button onClick={() => speak(currentTurn.prompt)} style={secondaryButton(colors)}><Volume2 size={17}/> Soruyu dinle</button>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {activeTurn > 0 && <button onClick={() => setActiveTurn((value) => value - 1)} style={secondaryButton(colors)}><ArrowLeft size={17}/> Geri</button>}
                    <button onClick={goNext} style={primaryButton}>{activeTurn === guidedTurns.length - 1 ? "Taslakları oluştur" : "Sonraki soru"}<ArrowRight size={17}/></button>
                  </div>
                </div>
                {recordingTarget && <div style={{ marginTop: 13, color: "#dc2626", fontWeight: 850, fontSize: 13 }}>● Dinliyorum… Duraklayabilir, sonra konuşmaya devam edebilirsiniz.</div>}
              </main>
            </div>
          ) : (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.35fr) minmax(300px,.65fr)", gap: 16, alignItems: "start" }}>
                <section style={panelStyle(colors)}>
                  <h4 style={panelTitle}><FileText size={18}/> Soru-cevap taslakları <span style={countBadge}>{qaDrafts.length}</span></h4>
                  <p style={{ color: colors.muted, margin: "0 0 13px", fontSize: 13 }}>Her kayıt ayrı ayrı düzenlenebilir. Kaydedildiğinde bilgi havuzuna “Taslak” olarak eklenir.</p>
                  <div style={{ display: "grid", gap: 12 }}>
                    {qaDrafts.map((draft, index) => (
                      <article key={index} style={{ border: `1px solid ${colors.border}`, borderRadius: 14, padding: 13, background: colors.subtle }}>
                        <label style={{ fontWeight: 800 }}>Soru {index + 1}<input value={draft.question} onChange={(event) => updateQADraft(index, "question", event.target.value)} style={inputStyle(colors)}/></label>
                        <label style={{ display: "block", fontWeight: 800, marginTop: 10 }}>Cevap<textarea value={draft.answer} onChange={(event) => updateQADraft(index, "answer", event.target.value)} style={{ ...inputStyle(colors), minHeight: index === 0 ? 190 : 110 }}/></label>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 9 }}><button onClick={() => onUseDraft?.(draft)} style={secondaryButton(colors)}>Bu kaydı formda aç</button></div>
                      </article>
                    ))}
                  </div>
                </section>

                <section style={panelStyle(colors)}>
                  <h4 style={panelTitle}><ListChecks size={18}/> Süreç şablonu</h4>
                  <label style={{ fontWeight: 800 }}>Süreç adı<input value={processDraft?.title || ""} onChange={(event) => setProcessDraft((current) => ({ ...current, title: event.target.value }))} style={inputStyle(colors)}/></label>
                  <label style={{ display: "block", fontWeight: 800, marginTop: 10 }}>Son tarih / tekrar<textarea value={processDraft?.deadline || ""} onChange={(event) => setProcessDraft((current) => ({ ...current, deadline: event.target.value }))} style={{ ...inputStyle(colors), minHeight: 82 }}/></label>
                  <div style={{ marginTop: 12, fontWeight: 850 }}>Görev adımları</div>
                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    {(processDraft?.steps || []).map((step, index) => <div key={index} style={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: 7, alignItems: "start" }}><span style={{ ...countBadge, marginTop: 9 }}>{index + 1}</span><textarea value={step.title} onChange={(event) => updateProcessStep(index, event.target.value)} style={{ ...inputStyle(colors), minHeight: 68 }}/></div>)}
                  </div>
                  {!processDraft?.steps?.length && <div style={{ color: "#b45309", background: "#fffbeb", padding: 10, borderRadius: 10, marginTop: 9, fontSize: 13 }}>Adımlar cevabını numaralı veya satır satır yazarak süreç adımlarını oluşturabilirsiniz.</div>}
                </section>
              </div>
              <div style={{ padding: 12, borderRadius: 12, background: colors.subtle, color: colors.muted, marginTop: 15, fontSize: 13, lineHeight: 1.5 }}>Kaydetme işlemi içerikleri yayımlamaz. Soru-cevaplar bilgi havuzunda, görev adımları Süreç Taslakları bölümünde ayrıca kontrol edilir.</div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 9, marginTop: 16, flexWrap: "wrap" }}>
                <button onClick={reset} style={secondaryButton(colors)}><RotateCcw size={17}/> Yeni oturum başlat</button>
                <div style={{ display: "flex", gap: 8 }}><button onClick={() => setStage("session")} style={secondaryButton(colors)}><ArrowLeft size={17}/> Görüşmeye dön</button><button onClick={saveAll} disabled={busy} style={{ ...primaryButton, opacity: busy ? .6 : 1 }}><Save size={17}/>{busy ? "Kaydediliyor…" : "Tümünü taslak olarak kaydet"}</button></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, zIndex: 5000, background: "rgba(15,23,42,.6)", backdropFilter: "blur(6px)", display: "grid", placeItems: "center", padding: 15 };
const primaryButton = { border: 0, borderRadius: 10, padding: "10px 13px", background: "#0f766e", color: "#fff", fontWeight: 850, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, cursor: "pointer" };
const recordingButton = { ...primaryButton, background: "#dc2626" };
const secondaryButton = (colors) => ({ ...primaryButton, background: colors.subtle, color: colors.text, border: `1px solid ${colors.border}` });
const iconButton = (colors) => ({ width: 36, height: 36, borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.subtle, color: colors.text, display: "grid", placeItems: "center", cursor: "pointer" });
const inputStyle = (colors) => ({ width: "100%", boxSizing: "border-box", marginTop: 7, padding: "11px 12px", resize: "vertical", borderRadius: 11, border: `1px solid ${colors.border}`, background: colors.subtle, color: colors.text, font: "inherit", lineHeight: 1.55 });
const stagePill = (active, colors) => ({ display: "inline-flex", alignItems: "center", gap: 7, borderRadius: 999, padding: "8px 12px", fontSize: 13, fontWeight: 850, background: active ? "#0f766e" : colors.subtle, color: active ? "#fff" : colors.text, border: `1px solid ${active ? "#0f766e" : colors.border}` });
const turnButton = (active, complete, colors) => ({ width: "100%", border: `1px solid ${active ? "#0f766e" : "transparent"}`, borderRadius: 11, background: active ? (colors.surface) : "transparent", color: colors.text, padding: "9px 10px", display: "flex", alignItems: "center", gap: 9, fontWeight: 800, cursor: "pointer", textAlign: "left", boxShadow: active ? "0 5px 15px rgba(15,118,110,.1)" : "none", opacity: complete || active ? 1 : .78 });
const panelStyle = (colors) => ({ border: `1px solid ${colors.border}`, borderRadius: 17, padding: 15, background: colors.surface });
const panelTitle = { margin: "0 0 7px", display: "flex", alignItems: "center", gap: 8 };
const countBadge = { minWidth: 23, height: 23, padding: "0 6px", borderRadius: 999, background: "#dff8f4", color: "#0f766e", display: "inline-grid", placeItems: "center", fontSize: 12, fontWeight: 900 };

export default VoiceKnowledgeBuilder;
